import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "../redux";
import { updateDownloadJobStatus } from "../redux/reducers/downloadJobs.reducer";
import downloadServices from "../redux/services/download.services";
import { IDownloadReportStatusResponse } from "../types";

export function useDownloadJobsManager() {
	const dispatch = useAppDispatch();
	const downloadJobs = useAppSelector((state) => state.downloadJobs.jobs);

	const intervalsRef = useRef<Map<string, number>>(new Map());
	const activeJobIdsRef = useRef<Set<string>>(new Set());

	function handleDownloadReport(jobID: string, downloadURL?: string, fileName?: string) {
		downloadServices
			.handleDownloadReport(jobID, downloadURL, fileName)
			.then(() => {
				dispatch(
					updateDownloadJobStatus({
						jobId: jobID,
						status: "completed",
						progress: 100,
						message: "Download complete",
						error: ""
					})
				);
				activeJobIdsRef.current.delete(jobID);
			})
			.catch((error) => {
				dispatch(
					updateDownloadJobStatus({
						jobId: jobID,
						status: "failed",
						error: typeof error === "string" ? error : String(error ?? "Download failed"),
						message: ""
					})
				);
				activeJobIdsRef.current.delete(jobID);
			});
	}

	function stopPolling(jobId: string) {
		const interval = intervalsRef.current.get(jobId);
		if (interval) {
			window.clearInterval(interval);
			intervalsRef.current.delete(jobId);
		}
	}

	function handleStatus(jobId: string, statusData: IDownloadReportStatusResponse) {
		switch (statusData.status) {
			case "PENDING":
			case "STARTED":
			case "PROGRESS":
				dispatch(
					updateDownloadJobStatus({
						jobId: jobId,
						status: "downloading",
						progress: statusData.progress ?? 0,
						message:
							statusData.status === "PENDING"
								? "Waiting to start..."
								: statusData.status === "STARTED"
								? "Report generation started"
								: `Generating report (${statusData.progress ?? 0}%)`
					})
				);
				break;
			case "SUCCESS":
				dispatch(
					updateDownloadJobStatus({
						jobId: jobId,
						status: "downloading",
						progress: 100,
						message: "Report generated successfully",
						error: ""
					})
				);
				stopPolling(jobId);
				handleDownloadReport(jobId, statusData.download_url, statusData.filename);
				break;
			case "FAILURE":
				dispatch(
					updateDownloadJobStatus({
						jobId: jobId,
						status: "failed",
						error: statusData.error ?? "Report generation failed",
						message: ""
					})
				);
				stopPolling(jobId);
				activeJobIdsRef.current.delete(jobId);
				break;
			default:
				break;
		}
	}

	function startPolling(jobId: string) {
		const pollStatus = async () => {
			try {
				const statusData = await downloadServices.getReportJobStatus(jobId);
				handleStatus(jobId, statusData);
			} catch (error) {
				dispatch(
					updateDownloadJobStatus({
						jobId: jobId,
						status: "failed",
						error: typeof error === "string" ? error : String(error ?? "Something went wrong"),
						message: ""
					})
				);
				stopPolling(jobId);
				activeJobIdsRef.current.delete(jobId);
			}
		};

		// Run immediately, then continue polling.
		pollStatus();
		const interval = window.setInterval(pollStatus, 3000);
		intervalsRef.current.set(jobId, interval);
	}

	useEffect(() => {
		const pendingJobs = downloadJobs.filter((job) => job.status === "pending" || job.status === "downloading");

		for (const job of pendingJobs) {
			if (activeJobIdsRef.current.has(job.id)) continue;

			activeJobIdsRef.current.add(job.id);

			dispatch(
				updateDownloadJobStatus({
					jobId: job.id,
					status: "downloading",
					message: job.status === "pending" ? "Waiting to start..." : "Reconnecting..."
				})
			);

			startPolling(job.id);
		}

		// Cleanup polling for jobs that are no longer active.
		const activeJobIds = new Set(pendingJobs.map((job) => job.id));
		activeJobIdsRef.current.forEach((jobId) => {
			if (!activeJobIds.has(jobId)) {
				stopPolling(jobId);
				activeJobIdsRef.current.delete(jobId);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [downloadJobs]);

	useEffect(() => {
		const intervals = intervalsRef.current;
		const activeJobIds = activeJobIdsRef.current;

		return () => {
			intervals.forEach((interval) => window.clearInterval(interval));
			intervals.clear();
			activeJobIds.clear();
		};
	}, []);
}

